import React, { useCallback, useEffect, useState } from 'react';
import { Button, Card, Table } from '@themesberg/react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSignOutAlt } from '@fortawesome/free-solid-svg-icons';
import { Link } from 'react-router-dom';
import PageHeader, { AlertMessage } from '../../components/PageHeader';
import AdminNav from '../../components/AdminNav';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { adminApi } from '../../services/api';
import { Routes } from '../../routes';

function formatTs(value) {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return String(value);
  }
}

export default function AdminTenants() {
  const { username, logout, accessToken } = useAdminAuth();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actingId, setActingId] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const load = useCallback(async () => {
    if (!accessToken) return;
    setError('');
    setLoading(true);
    try {
      const { data } = await adminApi.listTenants();
      setTenants(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Failed to load tenants');
      setTenants([]);
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    load();
  }, [load]);

  const handleDisable = async (id, name) => {
    if (!window.confirm(`Suspend tenant “${name}”? Login and JWTs will stop working until re-enabled.`)) {
      return;
    }
    setActingId(id);
    setError('');
    setSuccess('');
    try {
      await adminApi.disableTenant(id);
      setSuccess(`Suspended ${name}`);
      await load();
    } catch (err) {
      setError(err.message || 'Disable failed');
    } finally {
      setActingId('');
    }
  };

  const handleEnable = async (id, name) => {
    setActingId(id);
    setError('');
    setSuccess('');
    try {
      await adminApi.enableTenant(id);
      setSuccess(`Re-activated ${name}`);
      await load();
    } catch (err) {
      setError(err.message || 'Enable failed');
    } finally {
      setActingId('');
    }
  };

  return (
    <>
      <PageHeader
        title="Tenants"
        subtitle={`Platform admin${username ? ` · ${username}` : ''}`}
      >
        <Button variant="outline-secondary" size="sm" className="me-2" onClick={load} disabled={loading}>
          Refresh
        </Button>
        <Button variant="outline-danger" size="sm" onClick={logout}>
          <FontAwesomeIcon icon={faSignOutAlt} className="me-1" />
          Sign out
        </Button>
      </PageHeader>

      <AdminNav />

      <AlertMessage variant="danger" message={error} onClose={() => setError('')} />
      <AlertMessage variant="success" message={success} onClose={() => setSuccess('')} />

      <Card border="light" className="shadow-sm">
        <Card.Body className="p-0">
          <Table responsive hover className="align-middle mb-0">
            <thead className="thead-light">
              <tr>
                <th>Name</th>
                <th>ID</th>
                <th>Status</th>
                <th>Created</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5} className="text-center text-gray py-4">
                    Loading…
                  </td>
                </tr>
              ) : tenants.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center text-gray py-4">
                    No tenants yet.
                  </td>
                </tr>
              ) : (
                tenants.map((t) => (
                  <tr key={t.id}>
                    <td className="fw-bold">{t.name}</td>
                    <td>
                      <code className="small">{t.id}</code>
                    </td>
                    <td>
                      <span
                        className={`badge bg-${t.status === 'ACTIVE' ? 'success' : 'secondary'}`}
                      >
                        {t.status}
                      </span>
                    </td>
                    <td className="text-gray small">{formatTs(t.createdAt)}</td>
                    <td className="text-end text-nowrap">
                      <Button
                        as={Link}
                        to={Routes.AdminTenantDetail.path.replace(':id', t.id)}
                        size="sm"
                        variant="outline-primary"
                        className="me-2"
                      >
                        View
                      </Button>
                      {t.status === 'ACTIVE' ? (
                        <Button
                          size="sm"
                          variant="outline-danger"
                          disabled={actingId === t.id}
                          onClick={() => handleDisable(t.id, t.name)}
                        >
                          Suspend
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline-success"
                          disabled={actingId === t.id}
                          onClick={() => handleEnable(t.id, t.name)}
                        >
                          Enable
                        </Button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>
    </>
  );
}
